<template>
  <div
    class="ptr-root"
    @touchstart.passive="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
    @touchcancel="onTouchEnd"
  >
    <!-- Pull indicator — revealed as the content is dragged down -->
    <div
      class="ptr-indicator"
      :style="{ height: `${indicatorHeight}px`, opacity: indicatorHeight > 0 ? 1 : 0 }"
      aria-hidden="true"
    >
      <div
        v-if="refreshing"
        class="spinner ptr-spinner"
      ></div>
      <svg
        v-else
        class="ptr-arrow"
        :style="{ transform: `rotate(${arrowRotation}deg)`, opacity: pullProgress }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
      </svg>
    </div>

    <!-- Live region so screen-reader users hear the refresh outcome -->
    <div class="sr-only" role="status" aria-live="polite">{{ announcement }}</div>

    <div
      class="ptr-content"
      :style="contentStyle"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Capacitor } from "@capacitor/core"
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import { hapticMedium } from "../services/hapticsService"

const props = withDefaults(
  defineProps<{
    // Async work to run on pull. The spinner stays up until it resolves.
    onRefresh: () => Promise<void>
    // Pull distance (px) required to trigger a refresh.
    threshold?: number
    disabled?: boolean
  }>(),
  { threshold: 72, disabled: false },
)

const { t } = useI18n()

const pullDistance = ref(0)
const refreshing = ref(false)
const announcement = ref("")

let startY = 0
let tracking = false

// Only meaningful on touch devices — on desktop the gesture would just fight
// the mouse wheel, so we no-op there.
const isTouchDevice =
  Capacitor.isNativePlatform() || (typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches)

// Resistance curve: the bar gets progressively harder to pull so it never
// feels like it's running away from the finger.
const indicatorHeight = computed(() => (refreshing.value ? props.threshold : pullDistance.value))
const pullProgress = computed(() => Math.min(1, pullDistance.value / props.threshold))
const arrowRotation = computed(() => (pullDistance.value >= props.threshold ? 180 : 0))
const contentStyle = computed(() => ({
  transform: `translateY(${indicatorHeight.value}px)`,
  transition: tracking ? "none" : "transform 0.25s ease",
}))

function atTop(): boolean {
  return (window.scrollY || document.documentElement.scrollTop || 0) <= 0
}

function onTouchStart(e: TouchEvent) {
  if (props.disabled || !isTouchDevice || refreshing.value) return
  if (!atTop()) return
  startY = e.touches[0].clientY
  tracking = true
}

function onTouchMove(e: TouchEvent) {
  if (!tracking) return
  const delta = e.touches[0].clientY - startY
  // If the user scrolled up first or isn't at the top anymore, bail out so we
  // don't hijack a normal scroll.
  if (delta <= 0 || !atTop()) {
    tracking = false
    pullDistance.value = 0
    return
  }
  // Damp the pull so it eases toward a cap rather than tracking 1:1.
  const next = Math.min(props.threshold * 1.5, delta * 0.5)
  // Tick once when the pull crosses the release threshold (arrow flips).
  if (pullDistance.value < props.threshold && next >= props.threshold) hapticMedium()
  pullDistance.value = next
  // We're now clearly in a pull gesture — stop the page from rubber-banding.
  if (e.cancelable) e.preventDefault()
}

async function onTouchEnd() {
  if (!tracking) return
  tracking = false
  if (pullDistance.value >= props.threshold && !refreshing.value) {
    refreshing.value = true
    pullDistance.value = 0
    try {
      await props.onRefresh()
      announcement.value = t("common.refreshed")
    } catch {
      announcement.value = t("common.refresh_failed")
    } finally {
      refreshing.value = false
      // Clear the announcement shortly after so it doesn't linger in the DOM.
      window.setTimeout(() => {
        announcement.value = ""
      }, 1500)
    }
  } else {
    pullDistance.value = 0
  }
}
</script>

<style scoped>
.ptr-root {
  position: relative;
}

.ptr-indicator {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: var(--text-tertiary);
  pointer-events: none;
  z-index: 1;
}

.ptr-spinner {
  width: 24px;
  height: 24px;
}

.ptr-arrow {
  width: 22px;
  height: 22px;
  transition: transform 0.15s ease;
}

.ptr-content {
  will-change: transform;
}
</style>
